import { useMutation, useQuery, useQueryClient } from "react-query";
import { ConversationRequest, ExtendedDialogueConfig } from "../../types";

import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { shouldNotRetry404 } from "src/config/react-query";
import { trackEvent } from "src/config/posthog";

export const createDialogue = async ({
  conversation,
  project_id,
  create_project,
}: {
  conversation: ConversationRequest;
  project_id?: number;
  create_project?: boolean;
}): Promise<number> => {
  const url = `${nuclearn_api_url}/chat/create-dialogue`;

  const response = await axios.post(url, conversation, {
    params: {
      project_id,
      create_project,
    },
  });
  return response.data;
};

export const useCreateDialogue = () => {
  const queryClient = useQueryClient();

  const createDialogueMutation = useMutation({
    mutationFn: (data: {
      conversation: ConversationRequest;
      project_id?: number;
    }) => createDialogue(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["dialogues"]);
    },
  });

  return createDialogueMutation;
};

export interface ToolRunSummaryTool {
  name: string;
  status_message?: string;
}

export const getToolRunSummary = async (
  tools: ToolRunSummaryTool[],
): Promise<string> => {
  const url = `${nuclearn_api_url}/chat/tool-run-summary`;
  try {
    const response = await axios.post(url, { tools });
    return response.data?.summary ?? "";
  } catch {
    // Callers fall back to a count-based label — never surface an error state
    return "";
  }
};

/**
 * Descriptive one-line label for a rolled-up tool run (backend FastLLM).
 * Returns "" on failure/disabled so callers keep their count-based fallback.
 * Cached forever per tool-run composition — the same run never re-asks.
 */
export const useToolRunSummary = ({
  tools,
  enabled,
}: {
  tools: ToolRunSummaryTool[];
  enabled: boolean;
}) => {
  return useQuery({
    // Structured key — react-query hashes it deterministically, so
    // delimiter characters inside status messages can't collide.
    queryKey: ["toolRunSummary", tools],
    queryFn: () => getToolRunSummary(tools),
    enabled: enabled && tools.length > 0,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Durably annotate an interactive tool outcome onto the stored dialogue.
 * Load-bearing for delivery-failure recovery (WebSearchApproval's
 * `undelivered` marker rides on it), so failures are observable: resolves
 * true only when the write landed, retrying once on 404 (the caller may
 * race the emitting turn's persistence); non-404 failures — including the
 * 10s timeout — are logged and tracked, never silently swallowed.
 */
export const persistToolResponseOutcome = (
  dialogueId: number,
  promptId: string,
  outcome: Record<string, unknown>,
): Promise<boolean> => {
  const post = () =>
    axios.post(
      `${nuclearn_api_url}/chat/dialogue/${dialogueId}/tool-response-outcome`,
      { prompt_id: promptId, ...outcome },
      // No global axios timeout exists; a hung persist must count as failed.
      { timeout: 10_000 },
    );
  const report = (error: unknown) => {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    // console alone never leaves the browser; trackEvent does.
    console.error("[persistToolResponseOutcome:failed]", {
      dialogueId,
      promptId,
      status,
    });
    trackEvent("tool_outcome_persist_failed", {
      dialogueId,
      promptId,
      status: status ?? "network",
    });
  };
  return post().then(
    () => true,
    (error) => {
      if (
        (error as { response?: { status?: number } })?.response?.status !== 404
      ) {
        report(error);
        return false;
      }
      return new Promise<boolean>((resolve) => {
        setTimeout(() => {
          post().then(
            () => resolve(true),
            (retryError) => {
              report(retryError);
              resolve(false);
            },
          );
        }, 2000);
      });
    },
  );
};

export const getDialogueConfig = async ({
  dialogueId,
}: {
  dialogueId: number;
}): Promise<ExtendedDialogueConfig> => {
  const url = `${nuclearn_api_url}/chat/dialogue-config/${dialogueId}`;
  const response = await axios.get(url);
  return response.data;
};

export const useGetDialogueConfig = ({
  dialogueId,
}: {
  dialogueId: number | null;
}) => {
  return useQuery({
    queryKey: ["dialogueConfig", dialogueId],
    queryFn: () => getDialogueConfig({ dialogueId: dialogueId as number }),
    enabled: Boolean(dialogueId),
    // A 404 here means the dialogue is missing or not the caller's (backend
    // returns the same 404 for both — #30212); it's deterministic, so fail
    // fast to the access banner instead of retrying behind backoff.
    retry: shouldNotRetry404,
  });
};
